import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Link } from "react-router-dom"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { selectGameConfig } from "src/store/common"
import { playUiSound, primeUiSound } from "src/utils/audio"
import BaseLayout from "src/layouts/BaseLayout"
import useLocalizedPath from "src/hooks/useLocalizedPath"

import guideArt from "src/assets/images/units/zombie_zombie.webp"
import guideMark from "src/assets/images/zombie_icon.webp"
import humanSoldier from "src/assets/images/units/human_soldier.webp"
import humanRanger from "src/assets/images/units/human_ranger.webp"
import humanWorker from "src/assets/images/units/human_worker.webp"
import humanTank from "src/assets/images/units/human_tank.webp"
import zombieWalker from "src/assets/images/units/zombie_walker.webp"
import zombieTitan from "src/assets/images/units/zombie_titan.webp"
import zombieAmbusher from "src/assets/images/units/zombie_ambusher.webp"
import zombieDog from "src/assets/images/units/zombie_dog.webp"
import humanMedic from "src/assets/images/units/human_medic.webp"
import humanElite from "src/assets/images/units/human_elite.webp"
import zombieDestructor from "src/assets/images/units/zombie_destructor.webp"
import humanHelicopter from "src/assets/images/units/human_helicopter.webp"
import commandCenter from "src/assets/images/buildings/command_center.webp"
import hatchery from "src/assets/images/buildings/hatchery.webp"
import missileSilo from "src/assets/images/buildings/missile_silo.webp"
import tunnel from "src/assets/images/buildings/tunnel.webp"

import "./HowToPlay.css"

/* The manual, in three tiers, because a flat list of eleven equal cards answered every question
   except the first one somebody has: *what is this game*. An overview says what you are looking at
   and how a round feels, a callout says how it ends, and then each chapter leads with one plain
   sentence — the general behaviour — with the specifics as bullets underneath. A reader who stops
   after the summaries still knows how to play; the bullets are there for the second read.

   And it is *told* rather than listed (owner's ask, 2026-09-04): a zombie guide — the walker from
   the game's own unit art — opens the page, and every chapter carries one line from him. The
   rules did not change; the eight cards were correct and nobody read to the end of them. A joke
   at the top of a card is what gets a reader to the bullets underneath it. Each card is also
   illustrated with the plate of a unit or a building the chapter is about, so a first-time reader
   has seen a hatchery before the lobby asks them to pick a side.

   Static prose except where it quotes a number, and **every number comes from /game-config/**
   (owner's rule, 2026-09-04): the action budget, the settlement price, how many cities you start
   with, what water costs to cross and how many bot difficulties there are. A
   number on this screen is a claim about what the server will do, and reading the server's own
   copy is the only way to keep it true — the fallbacks this file used to carry had already
   drifted (the worker price said 100 while the server charged 150), which is what a second copy
   of a number does. There are no fallbacks now: the bootstrap loads the config before any route
   renders, so a missing key is a hole in a sentence rather than a wrong number in one. */

/* `sides` is the one chapter that is not a list. The asymmetry between the two economies is the
   most interesting thing about the game and it is a *comparison*, so it is drawn as two accented
   halves side by side rather than as bullets that happen to alternate.

   It leads for a layout reason as much as an editorial one: a full-width card anywhere else in an
   auto-fill grid starts a new row and leaves the two cells beside its predecessor empty. First, it
   is a banner. It also happens to be the question a reader has straight after the overview. */
export const CHAPTERS = ["sides", "rounds", "cities", "expansion", "combat", "fog", "specials", "survival", "allies", "classes"]

/* Nine regular chapters after the two-sides banner: three rows of three, every card a third.
   The two closing chapters used to share a row at half width each (owner, 2026-09-04) because
   eight regular chapters left two orphans on the last row; survival made it nine, and the half
   rule then broke the rhythm instead of keeping it — a third, a half and a card wrapped alone
   underneath (owner, 2026-09-16: "it should be 3 cols, as all the other sections"). If a chapter
   is ever added or removed, the count is what to check: a last row of one or two is what the
   half rule was for, and it would be the thing to bring back. */

/* Which plate a chapter shows, and how it is cropped. Every unit plate is a 9:10 sprite framed
   head to waist (humans 2026-09-05, zombies 2026-09-06), so the subject sits around a third of the
   way down and the crops say so.

   **The buildings are `wide`**, and that flag is the whole reason it exists: they were 1024x1024
   and were redrawn as 1456x720 scenes — a building standing in its own ruined block — so a pair
   cropped side by side showed the middle third of each and threw the scene away. A chapter whose
   plates are all wide stacks them instead, full width and half height each. Nothing else in here
   is wide, so nothing else changes.

   Two plates make a pair: the chapter about reaching further has one weapon per side, and cities
   are a command centre *and* a hatchery. */
const CHAPTER_ART = {
  sides: { humans: humanSoldier, zombies: zombieWalker },
  rounds: [{ src: humanRanger, focus: "50% 30%" }],
  cities: [
    { src: commandCenter, focus: "50% 50%", wide: true },
    { src: hatchery, focus: "50% 50%", wide: true },
  ],
  expansion: [{ src: humanWorker, focus: "50% 28%" }],
  combat: [
    { src: humanTank, focus: "50% 45%" },
    { src: zombieTitan, focus: "50% 30%" },
  ],
  fog: [{ src: zombieAmbusher, focus: "50% 40%" }],
  specials: [
    { src: missileSilo, focus: "50% 50%", wide: true },
    { src: tunnel, focus: "50% 50%", wide: true },
  ],
  /* Survival is the one chapter about a *mode*, and the mode is "they keep coming" — so it shows
     the two things that come, one a side. Either side can be the enemy there, which is the fact
     the pair is here to carry. */
  survival: [
    { src: zombieDestructor, focus: "50% 35%" },
    { src: humanElite, focus: "50% 30%" },
  ],
  allies: [{ src: humanMedic, focus: "50% 30%" }],
  /* **A class is its special unit**, so this chapter shows two of those plates — the same files
     the lobby's cards, the shop's cards and the game roster show, through `raceUnits`. It used to
     show `example_human_race.png` and `example_zombie_race.png`: a green man and a red one, flat
     monochrome, 2:3, drawn before the current art and matching no class in the game (owner,
     2026-09-10: "the classes section images are not updated"). Both files are deleted — nothing
     else referenced them — because a plate that belongs to no class is one nobody can keep true.
     The helicopter is the technologist's and the dog the intelligent's; the dog moved off the
     allies chapter, which now wears the economist's medic, so no plate appears twice on the
     page. */
  classes: [
    { src: humanHelicopter, focus: "50% 40%" },
    { src: zombieDog, focus: "50% 45%" },
  ],
}

/* The guide's line. The mark beside it is the zombie hand from the team icon set, drawn through a
   CSS mask so it takes the theme's red rather than the black the file ships in. Decorative — the
   words are the content, and a screen reader gets the words. */
const GuideSays = ({ children, className = "" }) => (
  <p className={`htp-guide-line ${className}`.trim()}>
    <span
      className="htp-guide-mark"
      style={{ WebkitMaskImage: `url(${guideMark})`, maskImage: `url(${guideMark})` }}
      aria-hidden="true"
    />
    <span className="htp-guide-words">{children}</span>
  </p>
)

/* Every placeholder the dictionaries may use, and where each comes from. `HowToPlay.test.js`
   reads this list against both dictionaries, so a `{{cities}}` typed into a Spanish line with no
   source here fails the suite rather than rendering as "{{cities}}" to a player. */
export const NUMBER_KEYS = [
  "actions",
  "cities",
  "settlementCost",
  "settlementWorkers",
  "waterCost",
  "difficulties",
  "keyResearchCost",
  "survivalPlayers",
  "survivalWaveRounds",
]

export const numbersFromConfig = (gameConfig) => {
  const rules = gameConfig?.rules_config
  return {
    actions: rules?.MAX_ACTIONS_PER_ROUND,
    cities: rules?.INITIAL_LOCATIONS,
    settlementCost: rules?.SETTLEMENT_COST,
    settlementWorkers: rules?.SETTLEMENT_COST_WORKERS,
    // "Costs double": the ratio, since it is the two constants' relationship the sentence is about.
    waterCost: rules?.MOVEMENT_COST_WATER && rules?.MOVEMENT_COST_LAND ? rules.MOVEMENT_COST_WATER / rules.MOVEMENT_COST_LAND : undefined,
    difficulties: gameConfig?.bot_difficulties?.length,
    // What the first level of a special-building program costs — every line opens at this price.
    keyResearchCost: rules?.RESEARCH_PROGRAM_COST_BASE,
    // Survival's seats and its wave cadence, off the same published block the lobby quotes (SV6).
    survivalPlayers: gameConfig?.survival?.MAX_PLAYERS,
    survivalWaveRounds: gameConfig?.survival?.WAVE_EVERY_ROUNDS,
  }
}

const ChapterPoints = ({ chapter, numbers, t }) => {
  const points = t(`howToPlay.chapters.${chapter}.points`, { ...numbers, returnObjects: true })
  // A dictionary out of step with this bundle would hand back the key itself rather than an array.
  if (!Array.isArray(points)) return null
  return (
    <ul className="htp-points">
      {points.map((point) => (
        <li key={point}>{point}</li>
      ))}
    </ul>
  )
}

const ChapterPlates = ({ plates }) => (
  <div
    className={`htp-plates htp-plates-${plates.length}${plates.every((plate) => plate.wide) ? " htp-plates-stacked" : ""}`}
    aria-hidden="true"
  >
    {plates.map(({ src, focus }) => (
      <img key={src} src={src} alt="" style={{ objectPosition: focus }} />
    ))}
  </div>
)

/* Each side stands beside its own economy: the soldier by the workers' paragraph, the walker by
   the meat. The plate is a backdrop with a scrim over it, the same device the home page uses at
   phone widths, so the text is read against a colour we control rather than against whichever
   pixels the art puts behind a glyph. */
const SidesChapter = ({ text }) => (
  <div className="htp-sides">
    {["humans", "zombies"].map((side) => (
      <div key={side} className={`htp-side htp-side-${side}`}>
        <img className="htp-side-plate" src={CHAPTER_ART.sides[side]} alt="" aria-hidden="true" />
        <div className="htp-side-copy">
          <h3 className="htp-side-title">{text(`howToPlay.chapters.sides.${side}.title`)}</h3>
          <p className="htp-card-body">{text(`howToPlay.chapters.sides.${side}.body`)}</p>
        </div>
      </div>
    ))}
    <span className="htp-versus" aria-hidden="true">
      VS
    </span>
  </div>
)

/* How long he grumbles for before going back to being helpful. */
const POKE_MS = 3200
/* The lunge, and it is the CSS animation's own length — the overlay is torn down the moment it
   ends, so the two numbers being one is what stops a still frame of a giant zombie hanging over
   the page. Most of it is the hold at full size (owner, 2026-09-10). */
const SCARE_MS = 950

/* A jump scare is exactly what `prefers-reduced-motion` is for, so the reader who asked not to be
   thrown around gets the sound and the line and no lunge. Asked at press time rather than at mount
   because the setting can change under a page that is already open. */
const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches

/* He talks (owner, 2026-09-10): the bubble types itself out a letter at a time rather than
   appearing written. `TYPE_MS` a character puts his welcome at about two seconds, which is a
   speaking pace — much faster and it is a flicker, much slower and a reader has finished the
   sentence before he has.

   It restarts whenever the line changes, which is what makes the poke and the scare read as him
   saying something new rather than as text being swapped underneath him.

   **The first line waits a second** (owner, 2026-09-10). The page arrives with the route's own
   500ms fade, so typing from the first frame is a sentence written during an entrance nobody has
   finished watching — the beat lets the screen settle, and then he notices you. Only the first: a
   pause before an answer is a pause, and a pause before every answer is a stutter. */
const TYPE_MS = 22
const FIRST_LINE_MS = 1000

const useTypedOut = (line, awake = true) => {
  const [shown, setShown] = useState(line)
  /* Set when a line **finishes**, not when one starts, and that is not a detail: StrictMode runs
     every effect twice on mount — run, clean up, run again — so a flag raised on the way in is
     already true the second time round and the opening pause is skipped. Raising it at the end
     makes the double invoke idempotent: both runs see "he has not spoken yet" and wait. */
  const hasSpoken = useRef(false)
  useEffect(() => {
    if (prefersReducedMotion()) {
      setShown(line)
      return undefined
    }
    setShown("")
    // Not yet: a guide below the fold types when the reader gets there, not when the page loads
    // (see `Guide`'s `wakeOnView`) — a sentence typed to nobody is a sentence found finished.
    if (!awake) return undefined
    let interval = null
    const start = () => {
      let index = 0
      interval = setInterval(() => {
        index += 1
        setShown(line.slice(0, index))
        if (index >= line.length) {
          clearInterval(interval)
          hasSpoken.current = true
        }
      }, TYPE_MS)
    }
    const kickoff = setTimeout(start, hasSpoken.current ? 0 : FIRST_LINE_MS)
    return () => {
      clearTimeout(kickoff)
      clearInterval(interval)
    }
  }, [line, awake])
  return shown
}

/* Whether an element has scrolled into view, once. `IntersectionObserver` is the right way to
   ask and the page's own scroll is the fallback: the manual scrolls inside its own column, and a
   document the browser is not painting (a background tab, an automated one) gets no intersection
   updates at all, so a scroll listener in the capture phase — which sees a nested scroller's
   events — asks the box directly. Where neither exists (the test runner's DOM) the answer is
   "yes", which is the top guide's answer anyway. */
const useInView = (ref) => {
  const [seen, setSeen] = useState(() => typeof IntersectionObserver === "undefined")
  useEffect(() => {
    if (seen || !ref.current) return undefined
    const check = () => {
      const box = ref.current?.getBoundingClientRect()
      if (box && box.top < window.innerHeight && box.bottom > 0) setSeen(true)
    }
    let observer = null
    if (typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setSeen(true)
      })
      observer.observe(ref.current)
    }
    window.addEventListener("scroll", check, { capture: true, passive: true })
    check()
    return () => {
      observer?.disconnect()
      window.removeEventListener("scroll", check, { capture: true })
    }
  }, [ref, seen])
  return seen
}

/* The guide: the plate, the bubble, and what pressing either does. He stands twice on the page
   (owner, 2026-09-16) — at the top with his welcome, and at the foot with his goodbye, which used
   to be a bare quote strip under the last row of cards and read as a caption that had lost its
   card. Same figure, same bubble, same joke when pressed; only the line differs, and the second
   one types itself out when the reader reaches it rather than when the page loads. */
const Guide = ({ text, lineKey, wakeOnView = false }) => {
  /* Poke the zombie and he says so (owner, 2026-09-10). It is his own speech bubble that changes
     rather than a dialog over the page: the bubble is where this page's voice already lives, so
     the joke lands as him talking instead of as the site interrupting to tell you he talked. He
     goes back to his own line after `POKE_MS`, and poking again restarts the clock rather than
     stacking timers. **Either half of him answers** (owner, 2026-09-11): the plate and the bubble
     are one hit area between them, so a reader who aims at the sentence he just said gets the
     reply the picture would have given — and on a phone the bubble is the larger of the two. */
  /* **And poke him again and he comes at you** (owner, 2026-09-10): the second press is a lunge
     out of the screen and the loudest sound in the catalogue. It resets afterwards, so the pair —
     warning, then scare — can be walked into again rather than being a thing that happens once
     per page load and never explains itself. */
  const [said, setSaid] = useState(null)
  const [scaring, setScaring] = useState(false)
  const pokes = useRef(0)
  const timers = useRef([])
  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms))
  useEffect(() => () => timers.current.forEach(clearTimeout), [])

  const poke = () => {
    timers.current.forEach(clearTimeout)
    timers.current = []
    if (pokes.current === 0) {
      pokes.current = 1
      // The scare is a recording, and the press that fires it is the next one: decode it now so it
      // lands on the frame the lunge starts rather than whenever the fetch comes back.
      primeUiSound("scare")
      setSaid("poke")
      later(() => {
        pokes.current = 0
        setSaid(null)
      }, POKE_MS)
      return
    }
    pokes.current = 0
    playUiSound("scare")
    setSaid("scare")
    if (!prefersReducedMotion()) {
      setScaring(true)
      later(() => setScaring(false), SCARE_MS)
    }
    later(() => setSaid(null), SCARE_MS + POKE_MS)
  }

  const anchor = useRef(null)
  const seen = useInView(anchor)
  const awake = !wakeOnView || seen
  const line = text(said ? `howToPlay.guide.${said}` : lineKey)
  const typed = useTypedOut(line, awake)

  return (
    <>
      {/* The plate bleeds off the right edge the way the home page's does, and the speech bubble
          sits over its shoulder rather than under it, because a caption under a picture is read
          as a caption and a bubble beside a face is read as the face talking. */}
      <div className={`htp-guide${awake ? " htp-guide-awake" : ""}`} ref={anchor}>
        <button type="button" className="htp-guide-poke" onClick={poke} aria-label={text("howToPlay.guide.pokeLabel")}>
          <img className="htp-guide-plate" src={guideArt} alt="" aria-hidden="true" />
        </button>
        {/* The bubble presses too (owner, 2026-09-11). The joke is that he answers back, so
            the thing a reader aims at is whichever half of him they were already looking at —
            and on a phone the bubble sits over the plate's shoulder and is the bigger target
            of the two. It is a pointer surface only: the button on the plate is still the one
            tab stop and the one thing announced, because two controls that do the same thing
            is one more than a keyboard reader needs to hear about. */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div className="htp-bubble" onClick={poke}>
          {/* Two copies of the line, and each is for one kind of reader. The typed one is
              `aria-hidden`, because a live region re-announcing a sentence every 22ms as it
              grows is not a sentence being read out. The whole line sits beside it, off
              screen and announced when it changes — which for a reader who cannot see him is
              the whole of what pressing did. */}
          <p className="htp-bubble-text">
            <span aria-hidden="true">{typed}</span>
            <span className="htp-bubble-full" aria-live="polite">
              {line}
            </span>
          </p>
          <p className="htp-bubble-sign">
            <span className="htp-bubble-name">{text("howToPlay.guide.name")}</span>
            <span className="htp-bubble-role">{text("howToPlay.guide.role")}</span>
          </p>
        </div>
      </div>

      {/* The lunge. Fixed over the page rather than a transform on the plate: at 300px in a
          corner, a scale big enough to be a scare is mostly off the side of its own column. It
          takes no pointer events and lives for exactly the animation.

          Through a portal to the body, not rendered in place (owner, 2026-09-20: the footer's
          lunge "does not occupy full screen as the one in the top"). A `position: fixed` box is
          fixed to the viewport only until an ancestor carries a transform, and then it is fixed
          to that ancestor — and the footer *keeps* one: its rise animation runs `both`, so the
          `translateY(0)` it ends on stays applied for the life of the page. The hero copy is
          in no such box, which is why only the second guide's scare was the size of a card. */}
      {scaring &&
        createPortal(
          <div className="htp-scare" aria-hidden="true">
            <img className="htp-scare-plate" src={guideArt} alt="" />
          </div>,
          document.body
        )}
    </>
  )
}

function HowToPlay() {
  const { t } = useTranslation()
  const localized = useLocalizedPath()
  const gameConfig = useSelector(selectGameConfig)
  const numbers = numbersFromConfig(gameConfig)
  // Every line on the page may quote a number, the guide's included, so every lookup carries them.
  const text = (key) => t(key, numbers)

  return (
    <BaseLayout>
      <div className="how-to-play with-scroll-bar vertical">
        <header className="htp-hero">
          <div className="htp-hero-copy">
            <p className="section-label htp-label">{text("howToPlay.label")}</p>
            <h1 className="htp-title">{text("howToPlay.title")}</h1>
            <p className="htp-intro">{text("howToPlay.intro")}</p>
            {/* Basic training, offered here on purpose (owner's ask, 2026-09-14): it opens by
                itself once, right after an account is made, and this is where it is found again.
                The manual is the page a player comes back to when they want the rules explained,
                and the tutorial is those rules with the buttons attached — so the door to it sits
                beside the way back, as the louder of the two. */}
            <p className="htp-training-lead">{text("howToPlay.trainingLead")}</p>
            <div className="htp-hero-links">
              <Link to={localized("/tutorial")} className="htp-back htp-training">
                {text("howToPlay.training")}
              </Link>
              <Link to={localized("/")} className="htp-back">
                {text("howToPlay.back")}
              </Link>
            </div>
          </div>

          <Guide text={text} lineKey="howToPlay.guide.intro" />
        </header>

        {/* Two columns, as wide as the chapter grid under it: what the game is on the left, how
            it ends on the right. It was one 860px column over a 1080px grid, which read as a
            panel that had stopped short of the margin everything else reached. */}
        <section className="htp-overview">
          <div className="htp-overview-what">
            <p className="htp-lead">{text("howToPlay.overview.lead")}</p>
            <p className="htp-overview-body">{text("howToPlay.overview.rounds")}</p>
            <GuideSays>{text("howToPlay.guide.overview")}</GuideSays>
          </div>
          <div className="htp-win">
            <p className="section-label">{text("howToPlay.win.label")}</p>
            <p className="htp-win-body">{text("howToPlay.win.body")}</p>
            <GuideSays className="htp-guide-line-win">{text("howToPlay.guide.win")}</GuideSays>
          </div>
        </section>

        <div className="htp-grid">
          {CHAPTERS.map((chapter, index) => (
            <section
              key={chapter}
              className={`htp-card${chapter === "sides" ? " htp-card-wide" : ""}`}
              style={{ "--i": index }}
            >
              {chapter !== "sides" && <ChapterPlates plates={CHAPTER_ART[chapter]} />}
              <div className="htp-card-head">
                <p className="htp-card-number">{String(index + 1).padStart(2, "0")}</p>
                <h2 className="htp-card-title">{text(`howToPlay.chapters.${chapter}.title`)}</h2>
              </div>
              <div className="htp-card-content">
                <p className="htp-card-summary">{text(`howToPlay.chapters.${chapter}.summary`)}</p>
                {chapter === "sides" ? (
                  <SidesChapter text={text} />
                ) : (
                  <ChapterPoints chapter={chapter} numbers={numbers} t={t} />
                )}
                <GuideSays>{text(`howToPlay.guide.chapters.${chapter}`)}</GuideSays>
              </div>
            </section>
          ))}
        </div>

        <footer className="htp-footer">
          {/* His goodbye, said the way his welcome is. */}
          <div className="htp-farewell">
            <Guide text={text} lineKey="howToPlay.guide.outro" wakeOnView />
          </div>
          {/* Two exits, because a reader who reached the bottom is one of two people: ready for
              a game, or wanting to press the buttons once before anybody is watching. */}
          <div className="htp-footer-links">
            <Link to={localized("/")} className="htp-back big">
              {text("howToPlay.play")}
            </Link>
            <Link to={localized("/tutorial")} className="htp-back big htp-training">
              {text("howToPlay.trainingAgain")}
            </Link>
          </div>
          {/* Both datasets are CC BY 4.0, which asks for attribution somewhere a reader can find
              it. This is the one public page with room for a line that is not about playing. */}
          <p className="htp-credits">{text("howToPlay.credits")}</p>
        </footer>
      </div>
    </BaseLayout>
  )
}

export default HowToPlay
